import { useState, useEffect, useCallback } from 'react';

export interface RouteHistoryEntry {
  passportCode: string;
  passportName: string;
  destinationCode: string;
  destinationName: string;
  ruleName: string;
  color: string;
  checkedAt: string; // ISO timestamp
}

const STORAGE_KEY = 'visahub_route_history';
const MAX_ENTRIES = 8;

function readStorage(): RouteHistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function writeStorage(entries: RouteHistoryEntry[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(entries)); }
  catch { /* storage quota exceeded — fail silently */ }
}

export function useRouteHistory() {
  const [history, setHistory] = useState<RouteHistoryEntry[]>([]);

  // Load on mount (client-only)
  useEffect(() => { setHistory(readStorage()); }, []);

  const addRoute = useCallback((entry: Omit<RouteHistoryEntry, 'checkedAt'>) => {
    setHistory(prev => {
      // Remove duplicate (same passport+destination), newest first
      const deduped = prev.filter(
        e => !(e.passportCode === entry.passportCode && e.destinationCode === entry.destinationCode)
      );
      const next = [{ ...entry, checkedAt: new Date().toISOString() }, ...deduped].slice(0, MAX_ENTRIES);
      writeStorage(next);
      return next;
    });
  }, []);

  const removeRoute = useCallback((passportCode: string, destinationCode: string) => {
    setHistory(prev => {
      const next = prev.filter(
        e => !(e.passportCode === passportCode && e.destinationCode === destinationCode)
      );
      writeStorage(next);
      return next;
    });
  }, []);

  const clearHistory = useCallback(() => {
    writeStorage([]);
    setHistory([]);
  }, []);

  return { history, addRoute, removeRoute, clearHistory };
}
